import { coercion, optional, object, literal, string, number, array } from '@recoiljs/refine';
import { ToolInteractionType, MouseButton } from 'generated/graphql';
import type { ToolInteractionUnion, ToolViewportClickInteraction } from 'generated/graphql';
import { stringLiteralsCodegen } from 'utils/refine';
import { MOUSE_BUTTON } from '../../../constants';
import { useEventAction } from '../../../modules/usePicker';
import type { PickerEvent } from '../../../modules/usePicker';
import { showToastMessage } from '../feedback';
import { interactionHttpRequestChecker } from '../checkers';
import {
  useSetCursor,
  toCssCursor,
  useRegisterCursor,
  DEFAULT_CURSOR_ID,
} from '../../../modules/cursor';
import type { TriggerToolInteraction } from '../ConfigBasedTool';
import { useRecoilCallback } from 'recoil';
import { isDrawingAnnotationSelector } from '../responses/startAnnotation';
import { useMemo } from 'react';

const viewportClickInteractionChecker = object({
  __typename: optional(literal('ToolViewportClickInteraction')),
  type: literal(ToolInteractionType.ViewportClick),
  id: string(),
  cursorIcon: string(),
  button: stringLiteralsCodegen(MouseButton),
  maxConcurrentRequests: number(),
  request: interactionHttpRequestChecker,
});

type UseViewportClickInteraction = {
  interactions: ReadonlyArray<Readonly<ToolInteractionUnion>>;
  triggerToolInteraction: TriggerToolInteraction;
  loading: boolean;
  toolId: string;
};

export function useViewportClickInteraction({
  toolId,
  interactions,
  triggerToolInteraction,
  loading,
}: UseViewportClickInteraction) {
  const viewportClickInteractions =
    coercion<ReadonlyArray<Readonly<ToolViewportClickInteraction>>>(
      array(viewportClickInteractionChecker)
    )(
      interactions.filter((interaction) => interaction.type === ToolInteractionType.ViewportClick)
    ) ?? [];

  const leftViewportClickInteraction = viewportClickInteractions.find(
    (interaction) => interaction.button === MouseButton.Left
  );
  const rightViewportClickInteraction = viewportClickInteractions.find(
    (interaction) => interaction.button === MouseButton.Right
  );
  const middleViewportClickInteraction = viewportClickInteractions.find(
    (interaction) => interaction.button === MouseButton.Middle
  );

  const cursor = leftViewportClickInteraction?.cursorIcon ?? '';
  const cursorConfig = useMemo(
    () => ({
      id: DEFAULT_CURSOR_ID,
      cursor: toCssCursor({ value: `url(${cursor})` }),
    }),
    [cursor]
  );

  useRegisterCursor(cursorConfig);
  const setCursor = useSetCursor();

  const handleClick = useRecoilCallback(
    ({ snapshot }) =>
      async (event: PickerEvent) => {
        const isDrawingAnnotation = await snapshot.getPromise(isDrawingAnnotationSelector);

        // if we are drawing an annotation, we don't want to trigger this interaction
        // if we clicked over an annotation, we don't want to trigger this interaction
        if (event.id != null || isDrawingAnnotation) {
          return;
        }

        let viewportClickInteraction;
        switch (event.button) {
          case MOUSE_BUTTON.LEFT:
            viewportClickInteraction = leftViewportClickInteraction;
            break;
          case MOUSE_BUTTON.MIDDLE:
            viewportClickInteraction = middleViewportClickInteraction;
            break;
          case MOUSE_BUTTON.RIGHT:
            viewportClickInteraction = rightViewportClickInteraction;
            break;
          default:
            return;
        }

        if (viewportClickInteraction == null) {
          // No interaction defined for this button
          return;
        }

        event.originalEvent?.stopPropagation();

        const maxConcurrentRequests = viewportClickInteraction.maxConcurrentRequests ?? 0;
        const currentConcurrentRequests = loading ? 1 : 0;
        if (maxConcurrentRequests !== 0 && currentConcurrentRequests >= maxConcurrentRequests) {
          // TODO: Scope this by interaction, right now it's global
          showToastMessage('Please wait for the current request to complete');
          return;
        }

        setCursor({ id: DEFAULT_CURSOR_ID, cursor: 'wait' });
        try {
          await triggerToolInteraction({
            toolId,
            toolInteractionId: viewportClickInteraction.id,
            toolInteractionType: viewportClickInteraction.type,
          });
        } catch (err: any) {
          // This is handled by the `useMutation` onError option
        } finally {
          setCursor(cursorConfig);
        }
      },
    [
      cursorConfig,
      leftViewportClickInteraction,
      loading,
      middleViewportClickInteraction,
      rightViewportClickInteraction,
      setCursor,
      toolId,
      triggerToolInteraction,
    ]
  );

  useEventAction('click', handleClick);
  useEventAction('contextMenu', handleClick);
}
